import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { api } from '../api/client.js'

export interface ClientRecord {
  id: string
  status: boolean
  locked: boolean
  group_id: string
  email: string
  full_name: string | null
  phone_number: string | null
  additionalFields: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface ClientSummary {
  total: number
  active: number
  inactive: number
  locked: number
}

export type ClientsView = 'list' | 'card'
export type ClientSortBy = 'full_name' | 'email' | 'phone_number' | 'status' | 'created_at' | 'updated_at'
export type ClientSortDir = 'asc' | 'desc'

const EMPTY_SUMMARY: ClientSummary = { total: 0, active: 0, inactive: 0, locked: 0 }
const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50]
const isValidPageSize = (v: number) => PAGE_SIZE_OPTIONS.includes(v)

/**
 * Loads clients from GET /admin/clients. Filter/sort/view/page state lives in
 * the URL (via useSearchParams) rather than local state, so the list survives
 * a tab switch-away/back and is shareable/deep-linkable — deliberately
 * different from useInboxConversations, which keeps that state internal.
 */
export function useClients() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [clients, setClients] = useState<ClientRecord[]>([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<ClientSummary>(EMPTY_SUMMARY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const search = searchParams.get('search') ?? ''
  const groupId = searchParams.get('group') ?? ''
  const status = searchParams.get('status') ?? ''
  const locked = searchParams.get('locked') ?? ''
  const dateFrom = searchParams.get('from') ?? ''
  const dateTo = searchParams.get('to') ?? ''
  const view: ClientsView = searchParams.get('view') === 'card' ? 'card' : 'list'
  const sortBy = (searchParams.get('sortBy') as ClientSortBy | null) ?? 'updated_at'
  const sortDir: ClientSortDir = searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc'
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10) || 1
  const rawPageSize = Number.parseInt(searchParams.get('limit') ?? String(DEFAULT_PAGE_SIZE), 10)
  const pageSize = isValidPageSize(rawPageSize) ? rawPageSize : DEFAULT_PAGE_SIZE

  const patchParams = useCallback((patch: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') next.delete(key)
        else next.set(key, value)
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setSearch = useCallback((v: string) => patchParams({ search: v, page: null }), [patchParams])
  const setGroupId = useCallback((v: string) => patchParams({ group: v, page: null }), [patchParams])
  const setStatus = useCallback((v: string) => patchParams({ status: v, page: null }), [patchParams])
  const setLocked = useCallback((v: string) => patchParams({ locked: v, page: null }), [patchParams])
  const setDateRange = useCallback((from: string, to: string) => patchParams({ from, to, page: null }), [patchParams])
  const setView = useCallback((v: ClientsView) => patchParams({ view: v }), [patchParams])
  const setSort = useCallback((by: ClientSortBy, dir: ClientSortDir) => patchParams({ sortBy: by, sortDir: dir }), [patchParams])
  const setPage = useCallback((p: number) => patchParams({ page: p > 1 ? String(p) : null }), [patchParams])
  const setPageSize = useCallback((size: number) => {
    const next = isValidPageSize(size) ? size : DEFAULT_PAGE_SIZE
    patchParams({ limit: next === DEFAULT_PAGE_SIZE ? null : String(next), page: null })
  }, [patchParams])
  const clearFilters = useCallback(() => patchParams({
    search: null, group: null, status: null, locked: null, from: null, to: null, page: null,
  }), [patchParams])

  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (groupId) params.set('group_id', groupId)
    if (status) params.set('status', status)
    if (locked) params.set('locked', locked)
    if (dateFrom) params.set('from', dateFrom)
    if (dateTo) params.set('to', dateTo)
    params.set('sortBy', sortBy)
    params.set('sortDir', sortDir)
    params.set('page', String(page))
    params.set('limit', String(pageSize))
    return params.toString()
  }, [search, groupId, status, locked, dateFrom, dateTo, sortBy, sortDir, page])

  const fetchIdRef = useRef(0)

  const refetch = useCallback(async (): Promise<void> => {
    const fetchId = ++fetchIdRef.current
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ clients: ClientRecord[]; total: number; summary: ClientSummary }>(`/admin/clients?${query}`)
      if (fetchId !== fetchIdRef.current) return
      setClients(res.clients ?? [])
      setTotal(res.total ?? 0)
      setSummary(res.summary ?? EMPTY_SUMMARY)
    } catch (e) {
      if (fetchId !== fetchIdRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load clients')
      setClients([])
      setTotal(0)
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false)
    }
  }, [query])

  useEffect(() => { void refetch() }, [refetch])

  return {
    clients, total, summary, loading, error, refetch,
    search, setSearch,
    groupId, setGroupId,
    status, setStatus,
    locked, setLocked,
    dateFrom, dateTo, setDateRange,
    view, setView,
    sortBy, sortDir, setSort,
    page, pageSize, setPage, setPageSize,
    clearFilters,
  }
}
